import { RENDER_STATUS } from '../constants/constants';
import { apiGetDownloadRendersImagesV2, apiGetPreviewAndMainRender } from '../helpers/api';
import {
  download as fileDownload,
  downloadPrepared,
} from '../common/DownloadFileController/downloadFile';
import { useState } from 'react';
import { warningToast } from "../helpers/toastHelper";
import { IMAGE_TYPE } from '../constants/renderPreviewConstants';
import JSZip from 'jszip';
import JSZipUtils from '../../node_modules/jszip-utils/dist/jszip-utils.js';
import { renameDuplicateNames } from '../helpers/jsHelper.js';
import {
  fileNameFromUrl,
  postEditExtension,
  renderPostEditImages,
  revokePostEditImages,
} from '../helpers/postEditImageHelper';

/** JSZipUtils is callback based; everything around it here is promises. */
const getBinaryContent = (url) =>
  new Promise((resolve, reject) =>
    JSZipUtils.getBinaryContent(url, (error, data) => (error ? reject(error) : resolve(data)))
  );

/**
 * The shared files list returns each camera with a thumbnail and its saved
 * edits and nothing else - no Standard, Filmic or Colour Pass file, and so
 * nothing for the editor to draw the Post Edit pass over. A render off that
 * list has to be fetched in full before anything can be collected; one that
 * already carries its files is left alone.
 */
const withPassFiles = async (render) => {
  const hasFiles = (render?.images || []).some(
    (camera) =>
      camera?.file || camera?.extra_file || camera?.filmic_file || camera?.extra_filmic_file
  );
  if (hasFiles) return render;

  try {
    const full = await apiGetPreviewAndMainRender(
      render?.id,
      null,
      null,
      `?is_automation=${!!render?.is_automation}`
    );
    // The list's name wins - renameDuplicateNames may have just changed it.
    return full?.images?.length ? { ...full, name: render?.name } : render;
  } catch (error) {
    console.error('Failed to fetch the full render for download:', error);
    return render;
  }
};

export const useDownloadEntity = () => {
  const [loading, setLoading] = useState(false);

  async function download({ id, type, name, item }) {
    setLoading(true);
    if (type === 'renders-previews') {
      if(item){
        await setAngleImageUrl({is_separate:true , selectedList:[item]})
      }else{
        await apiGetDownloadRendersImagesV2({ ids: [id], fileName: name || 'downloaded-images.zip' })
      }
    }
    setLoading(false)
  }

  /**
   * Zips every collected image and saves it.
   *
   * Routed through downloadPrepared so the downloads snackbar carries a row for
   * the whole job: fetching each pass of each angle - and drawing the Post Edit
   * ones - takes long enough that a silent wait looked like nothing happening.
   */
  const handleDownloadData = (imageUrl, fileInfo, name) => {
    if (!imageUrl?.length) return undefined;

    const fileName = name?.toLowerCase().endsWith('.zip') ? name : `${name || 'render'}.zip`;

    return downloadPrepared({
      fileName,
      sectionName: name,
      prepare: async () => {
        const zip = new JSZip();

        await Promise.all(
          imageUrl.map(async (imgObj) => {
            const data = await getBinaryContent(imgObj.url);
            const ext = imgObj.filename.split('.').pop();
            const filename = `render_${imgObj.angle_name}_${imgObj.imageType}.${ext}`;
            zip.folder(imgObj?.itemFileName || fileInfo?.name).file(filename, data, { binary: true });
          })
        );

        const content = await zip.generateAsync({ type: 'blob' });
        return URL.createObjectURL(content);
      },
    });
  };

  /** Every downloadable pass of one camera, in the order they are offered. */
  const collectCameraImages = (camera, render, postEdit) => {
    const images = [];
    const add = (url, imageType, filename) => {
      if (!url) return;
      images.push({
        id: camera?.id,
        angle_name: camera?.angle_name,
        imageType,
        filename: filename || fileNameFromUrl(url),
        url,
        itemFileName: render?.name,
      });
    };

    add(camera?.file, IMAGE_TYPE.STANDARD);
    add(camera?.filmic_file, IMAGE_TYPE.FILMIC);
    add(camera?.color_pass_file, IMAGE_TYPE.COLOR);
    // A blob: URL carries no name, so the extension comes from what the editor
    // encoded it as.
    if (postEdit)
      add(postEdit.url, IMAGE_TYPE.POST_EDIT, `post_edit.${postEditExtension(postEdit.source)}`);

    return images;
  };

  /**
   * Downloads every pass of every angle of the given renders: the stored
   * Standard, Filmic and Colour Pass files, plus Post Edit for any angle that
   * has saved edits. `is_separate` gives each render its own zip.
   */
  const setAngleImageUrl = async ({ is_separate, selectedList }) => {
    const list = await Promise.all(
      renameDuplicateNames(selectedList || [], 'name').map(withPassFiles)
    );
    // The Post Edit pass has no stored file, so every edited camera is drawn
    // first - then collecting the files stays a plain loop.
    const postEdits = await renderPostEditImages(list);

    const collectImages = (render) =>
      (render?.images || []).flatMap((camera) =>
        collectCameraImages(camera, render, postEdits.get(camera?.id))
      );

    try {
      if (is_separate) {
        for (const render of list) {
          await handleDownloadData(collectImages(render), render, render?.name);
        }
      } else {
        await handleDownloadData(list.flatMap(collectImages), null, 'render');
      }
    } finally {
      revokePostEditImages(postEdits);
    }
  };

  async function download3DFilesZip(data) {
    if (data?.zip_file_details && data?.zip_file_details?.status === RENDER_STATUS.STATUS_DONE && data?.zip_file_details?.zip_file) {
      setLoading(true);
      await fileDownload({
        fileUrl:data?.zip_file_details?.zip_file,
        fileName: `${data?.name || 'downloaded-3d-files-zip'}.zip`,
        auth: false,
      });
      setLoading(false);
    } else {
      warningToast('Something went wrong!');
    }
  }

  return { download, loading, download3DFilesZip, handleDownloadData, setAngleImageUrl };
};
